// The Benchmark section of the Sentinel Workspace: golden cases to answer with form 1.6. The list has To do,
// In progress and Done tabs (plus Progress for editors); `&case=<id>` in the URL opens one case.

import { useMemo, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { useSearchParams } from 'react-router';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GoldenAdapter } from '@/adapters/golden.adapter';
import { getApiErrorMessage } from '@/lib/utils';
import type { GoldenCaseRow, GoldenMe, GoldenTab } from '@/types/golden.types';
import { BenchmarkCase } from './benchmark-case';
import { BenchmarkProgress } from './benchmark-progress';
import { benchmarkListQueryAtom, benchmarkTabAtom, benchmarkTodoQueryAtom, goldenMeQueryAtom } from './benchmark-state';

export const CASE_PARAM = 'case';
// Radix Select has no empty value, so "all" is a value of its own.
const ALL = 'all';
const TABS: { value: GoldenTab; label: string }[] = [
  { value: 'todo', label: 'To do' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'done', label: 'Done' },
];
const STATUS_LABELS: Record<GoldenTab, string> = { todo: 'To do', in_progress: 'In progress', done: 'Done' };
export const EMPTY_TODO = 'No cases waiting. New cases arrive every morning (UTC).';

type Filters = { account: string; day: string };
const matches = (row: GoldenCaseRow, filters: Filters) =>
  (filters.account === ALL || String(row.accountId) === filters.account) && (filters.day === ALL || row.frameDay === filters.day);

function CaseTable({ rows, onOpen }: { rows: GoldenCaseRow[]; onOpen: (id: string) => void }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Brand</TableHead>
          <TableHead>Frame day</TableHead>
          <TableHead>Case</TableHead>
          <TableHead>Channel</TableHead>
          <TableHead>Position</TableHead>
          <TableHead>Received</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>{row.brand}</TableCell>
            <TableCell className="tabular-nums">{row.frameDay}</TableCell>
            <TableCell>
              {/* To do cases open only through Start next, in the reviewer's fixed order. */}
              {row.status === 'todo' ? (
                row.shortId
              ) : (
                <Button variant="link" className="h-auto p-0 text-xs" onClick={() => onOpen(row.id)}>
                  {row.shortId}
                </Button>
              )}
            </TableCell>
            <TableCell>{row.channel}</TableCell>
            <TableCell>{row.position}</TableCell>
            <TableCell className="tabular-nums">{row.received}</TableCell>
            <TableCell>{STATUS_LABELS[row.status]}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function FilterSelect({ label, value, options, onChange }: { label: string; value: string; options: [string, string][]; onChange: (value: string) => void }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" aria-label={label} className="w-[160px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map(([optionValue, optionLabel]) => (
          <SelectItem key={optionValue} value={optionValue}>
            {optionLabel}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function BenchmarkList({ me, onOpen, viewSwitch }: { me: GoldenMe; onOpen: (id: string) => void; viewSwitch: React.ReactNode }) {
  const [tab, setTab] = useAtom(benchmarkTabAtom);
  const [showProgress, setShowProgress] = useState(false);
  const [filters, setFilters] = useState<Filters>({ account: ALL, day: ALL });
  const list = useAtomValue(benchmarkListQueryAtom);
  const todo = useAtomValue(benchmarkTodoQueryAtom);
  const counts = list.data?.counts ?? todo.data?.counts;
  const isEditor = me.role === 'editor';

  const rows = useMemo(() => (list.data?.rows ?? []).filter((row) => matches(row, filters)), [list.data, filters]);
  const days = useMemo(() => [...new Set((list.data?.rows ?? []).map((row) => row.frameDay))].sort().reverse(), [list.data]);
  const next = todo.data?.rows.find((row) => matches(row, filters));

  return (
    <div className="flex h-full w-full min-w-0 flex-col gap-4 overflow-y-auto p-4">
      <div className="flex flex-wrap items-center gap-3">
        {viewSwitch}
        <div className="ml-auto">
          <Button size="sm" disabled={!next} onClick={() => next && onOpen(next.id)}>
            Start next
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Tabs
          value={showProgress ? 'progress' : tab}
          onValueChange={(value) => {
            setShowProgress(value === 'progress');
            if (value !== 'progress') setTab(value as GoldenTab);
          }}
        >
          <TabsList>
            {TABS.map((option) => (
              <TabsTrigger key={option.value} value={option.value}>
                {option.label}
                {counts && <span className="ml-1 tabular-nums text-gray-700">{counts[option.value]}</span>}
              </TabsTrigger>
            ))}
            {isEditor && <TabsTrigger value="progress">Progress</TabsTrigger>}
          </TabsList>
        </Tabs>
        {!showProgress && me.accounts.length > 1 && (
          <FilterSelect
            label="Brand"
            value={filters.account}
            onChange={(account) => setFilters({ ...filters, account })}
            options={[[ALL, 'All brands'], ...me.accounts.map((id): [string, string] => [String(id), GoldenAdapter.brandName(id)])]}
          />
        )}
        {/* Kept while a day is chosen, so a tab without that day can still clear it. */}
        {!showProgress && (days.length > 1 || filters.day !== ALL) && (
          <FilterSelect
            label="Frame day"
            value={filters.day}
            onChange={(day) => setFilters({ ...filters, day })}
            options={[[ALL, 'All days'], ...days.map((day): [string, string] => [day, day])]}
          />
        )}
      </div>
      {showProgress ? (
        <BenchmarkProgress />
      ) : list.isError ? (
        <p role="alert" className="text-[13px] text-red-600">
          {getApiErrorMessage(list.error, 'Could not load the cases.')}
        </p>
      ) : list.isPending ? (
        <Skeleton className="h-40 w-full rounded-lg" />
      ) : rows.length === 0 ? (
        <p className="py-10 text-center text-[13px] text-gray-800">{tab === 'todo' ? EMPTY_TODO : 'No cases here yet.'}</p>
      ) : (
        <CaseTable rows={rows} onOpen={onOpen} />
      )}
    </div>
  );
}

/** `viewSwitch`: the workspace's view picker, which is also the way back to the other views; it names the page. */
export function BenchmarkPanel({ viewSwitch }: { viewSwitch: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const me = useAtomValue(goldenMeQueryAtom);
  const caseId = searchParams.get(CASE_PARAM);
  const openCase = (id: string | null) =>
    setSearchParams((previous) => {
      const params = new URLSearchParams(previous);
      if (id) params.set(CASE_PARAM, id);
      else params.delete(CASE_PARAM);
      return params;
    });

  if (me.isPending) return <Skeleton className="m-4 h-40 w-full rounded-lg" />;
  if (me.isError) {
    return (
      <div className="flex w-full flex-col items-start gap-3 p-4">
        {viewSwitch}
        <p role="alert" className="text-[13px] text-red-600">
          {getApiErrorMessage(me.error, 'Could not check your access to Benchmark.')}
        </p>
        <Button variant="outline" size="sm" onClick={() => void me.refetch()}>
          Retry
        </Button>
      </div>
    );
  }
  if (me.data.role === 'none') {
    return (
      <div className="flex w-full flex-col gap-4 p-4">
        {viewSwitch}
        <p className="text-[13px] text-gray-800">Benchmark is only for the Sentinels of its brands.</p>
      </div>
    );
  }
  if (caseId) return <BenchmarkCase key={caseId} caseId={caseId} me={me.data} onOpen={openCase} viewSwitch={viewSwitch} />;
  return <BenchmarkList me={me.data} onOpen={openCase} viewSwitch={viewSwitch} />;
}
